import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Row, Col, Form, Card, Badge, Spinner } from 'react-bootstrap';
import { NotificationManager } from 'react-notifications';
import { FiArrowLeft, FiSave, FiTrash2, FiDollarSign, FiRefreshCw, FiFileText, FiUser, FiCalendar, FiHash } from 'react-icons/fi';
import { PDFDownloadLink } from '@react-pdf/renderer';

import { AppContext } from '../AppContext';
import { tre } from '../trebus';
import PaymentPDF from './PaymentPDF';

export default function Payment() {
    const { id } = useParams();
    const navigate = useNavigate();
    const context = useContext(AppContext);
    const [payment, setPayment] = useState({ user: {} });
    const [deleted, setDeleted] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isCollecting, setIsCollecting] = useState(false);
    const [isRegenerating, setIsRegenerating] = useState(false);

    // Load payment
    const load = () => {
        setIsLoading(true);
        tre.api('GET', '/payments/' + id, {
            context: context,
            success: (r) => {
                setPayment(r);
                setIsLoading(false);
            },
            error: () => setIsLoading(false)
        });
    };

    const handleForm = (ev) => {
        setPayment({ ...payment, [ev.target.name]: ev.target.value });
    };

    const save = () => {
        setIsSaving(true);
        tre.api('PUT', '/payments/' + payment.id, {
            context: context,
            params: payment,
            success: () => {
                NotificationManager.success('Cobro modificado');
                setIsSaving(false);
            },
            error: () => setIsSaving(false)
        });
    };

    const remove = () => {
        if (!window.confirm('¿Estás seguro de eliminar este cobro?')) return;
        setIsDeleting(true);
        tre.api('DELETE', '/payments/' + payment.id, {
            context: context,
            success: () => {
                NotificationManager.success('Cobro eliminado');
                setDeleted(true);
                setIsDeleting(false);
            },
            error: () => setIsDeleting(false)
        });
    };

    const collect = () => {
        setIsCollecting(true);
        tre.api('PUT', '/payments/' + payment.id + '/collect', {
            context: context,
            success: (r) => {
                NotificationManager.success('Cobrado');
                setPayment(r);
                setIsCollecting(false);
            },
            error: () => setIsCollecting(false)
        });
    };

    const regenerate = () => {
        if (!window.confirm('¿Regenerar el cobro? Esto recalculará el importe.')) return;
        setIsRegenerating(true);
        tre.api('POST', '/fees/users/' + payment.user.id + '/payments', {
            context: context,
            params: {
                year: payment.year,
                month: payment.month
            },
            success: () => {
                setIsRegenerating(false);
                load();
            },
            error: () => setIsRegenerating(false)
        });
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const monthNames = ['', 'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
        'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

    // -----| Render |-----
    if (isLoading) {
        return (
            <div className="container py-4" style={{ maxWidth: '800px' }}>
                <div className="text-center py-5">
                    <Spinner animation="border" style={{ color: '#1a73e8' }} />
                    <p className="mt-3 text-muted">Cargando cobro...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="container py-4" style={{ maxWidth: '800px' }}>
            {/* Header */}
            <div className="d-flex align-items-center mb-4">
                <Button
                    variant="link"
                    onClick={() => navigate('/payments')}
                    className="p-0 me-3"
                    style={{ color: '#5f6368' }}
                >
                    <FiArrowLeft size={24} />
                </Button>
                <div className="flex-grow-1">
                    <h4 className="mb-0" style={{ color: '#202124', fontWeight: 500 }}>
                        <FiDollarSign className="me-2" style={{ color: '#1a73e8' }} />
                        Detalle del Cobro
                    </h4>
                    <small className="text-muted">
                        {monthNames[payment.month]} {payment.year}
                    </small>
                </div>
                <Badge
                    bg={payment.is_paid ? 'success' : 'warning'}
                    style={{ fontSize: '13px', padding: '8px 16px', fontWeight: 500 }}
                >
                    {payment.is_paid ? 'Pagado' : 'Pendiente'}
                </Badge>
            </div>

            {/* User Card */}
            <Card className="mb-4">
                <Card.Header className="py-3" style={{ backgroundColor: '#f8f9fa' }}>
                    <div className="d-flex align-items-center">
                        <div
                            className="d-flex align-items-center justify-content-center rounded-circle me-3"
                            style={{
                                width: '40px',
                                height: '40px',
                                backgroundColor: '#e8f0fe',
                                color: '#1a73e8'
                            }}
                        >
                            <FiUser size={18} />
                        </div>
                        <div>
                            <h6 className="mb-0" style={{ color: '#202124', fontWeight: 500 }}>Usuario</h6>
                            <small className="text-muted">
                                {payment.user?.surname ? `${payment.user.surname}, ${payment.user.name}` : payment.user?.name || '-'}
                            </small>
                        </div>
                    </div>
                </Card.Header>
                <Card.Body className="p-4">
                    <Row className="g-3">
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label style={{ fontWeight: 500, color: '#202124', fontSize: '13px' }}>
                                    <FiHash className="me-1" /> ID
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    value={payment.id || ''}
                                    disabled
                                    style={{ backgroundColor: '#f8f9fa' }}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label style={{ fontWeight: 500, color: '#202124', fontSize: '13px' }}>
                                    <FiCalendar className="me-1" /> Periodo
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    value={`${monthNames[payment.month]} ${payment.year}`}
                                    disabled
                                    style={{ backgroundColor: '#f8f9fa' }}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label style={{ fontWeight: 500, color: '#202124', fontSize: '13px' }}>
                                    <FiDollarSign className="me-1" /> Importe
                                </Form.Label>
                                <div className="input-group">
                                    <Form.Control
                                        type="number"
                                        step="0.01"
                                        name="price"
                                        value={payment.price || ''}
                                        onChange={handleForm}
                                    />
                                    <span className="input-group-text">€</span>
                                </div>
                            </Form.Group>
                        </Col>
                        <Col md={12}>
                            <Form.Group>
                                <Form.Label style={{ fontWeight: 500, color: '#202124', fontSize: '13px' }}>
                                    Concepto
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    name="concept"
                                    value={payment.concept || ''}
                                    onChange={handleForm}
                                    placeholder="Descripción del cobro"
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Actions */}
            {!deleted ? (
                <div className="d-flex justify-content-between flex-wrap gap-2">
                    <Button
                        variant="light"
                        onClick={() => navigate('/payments')}
                        style={{ padding: '10px 24px' }}
                    >
                        Cancelar
                    </Button>
                    <div className="d-flex flex-wrap gap-2">
                        <Button
                            variant="outline-danger"
                            onClick={remove}
                            disabled={isDeleting}
                            style={{ padding: '10px 20px' }}
                        >
                            {isDeleting ? <Spinner size="sm" className="me-2" /> : <FiTrash2 className="me-2" />}
                            Eliminar
                        </Button>

                        {!payment.is_paid && (
                            <>
                                <Button
                                    variant="warning"
                                    onClick={regenerate}
                                    disabled={isRegenerating}
                                    style={{ padding: '10px 20px' }}
                                >
                                    {isRegenerating ? <Spinner size="sm" className="me-2" /> : <FiRefreshCw className="me-2" />}
                                    Regenerar
                                </Button>
                                <Button
                                    variant="success"
                                    onClick={collect}
                                    disabled={isCollecting}
                                    style={{ padding: '10px 20px' }}
                                >
                                    {isCollecting ? <Spinner size="sm" className="me-2" /> : <FiDollarSign className="me-2" />}
                                    Cobrar
                                </Button>
                            </>
                        )}

                        {payment.user && payment.user.full_name && (
                            <PDFDownloadLink
                                document={<PaymentPDF payments={[payment]} />}
                                fileName="recibo.pdf"
                                style={{ textDecoration: 'none' }}
                            >
                                <Button variant="info" style={{ padding: '10px 20px' }}>
                                    <FiFileText className="me-2" />
                                    Descargar Recibo
                                </Button>
                            </PDFDownloadLink>
                        )}

                        <Button
                            variant="primary"
                            onClick={save}
                            disabled={isSaving}
                            style={{ padding: '10px 24px' }}
                        >
                            {isSaving ? <Spinner size="sm" className="me-2" /> : <FiSave className="me-2" />}
                            Guardar
                        </Button>
                    </div>
                </div>
            ) : (
                <div className="text-center py-4">
                    <p className="text-muted mb-3">Este cobro ha sido eliminado.</p>
                    <Button variant="outline-primary" onClick={() => navigate('/payments')}>
                        Volver a la lista
                    </Button>
                </div>
            )}
        </div>
    );
}
